import React, { useRef } from 'react';
import { Table } from 'js/components/common/Table';
import { useDeviceSelector } from 'js/store/hooks';
import { useEffectOnce } from 'js/hooks/useEffectOnce';
import { useUpdateEffect } from 'js/hooks/useUpdateEffect';
import { compose, pick, prop } from 'js/utils/ramda.util';
import { componentService } from 'js/services/component.service';
import { withComponent } from 'js/components/hocs/withComponent';
import { TABLE_VIEW } from 'js/components/common/Table/constants';
import { useComponentConfig } from 'js/hooks/component/useComponentConfig';
import { useCommonTableWidthObserver } from 'js/hooks/useCommonTableWidthObserver';
import { useComponentTranslations } from 'js/hooks/component/useComponentTranslations';

import * as hooks from './hooks';
import * as helpers from './helpers';
import * as actions from './store/actions';
import { DETAILED_TRANSACTION_HISTORY, MOBILE_VIEW_WIDTH_THRESHOLD } from './constants';
import { useDynamicReducerSelector, withDynamicReducer, useDynamicReducerDispatch } from './store';

import * as style from './DetailedTransactionHistory.scss';

export const DetailedTransactionHistoryBare = () => {
    const dispatch = useDynamicReducerDispatch();
    const translate = useComponentTranslations();
    const isMobile = useDeviceSelector(prop('isMobile'));
    const { showTotalTransactionHistory, showBeginEndBalanceEnabled } = useComponentConfig();
    const {
        isSummaryLoading, isHistoryLoading, summaryError,
        historyError, exportError, page: { isPageChanged }
    } = useDynamicReducerSelector(pick([
        'isSummaryLoading', 'isHistoryLoading', 'summaryError',
        'historyError', 'exportError', 'page'
    ]));
    const tableRef = useRef();
    const isMobileView = useCommonTableWidthObserver(tableRef, MOBILE_VIEW_WIDTH_THRESHOLD);
    const isLoading = showTotalTransactionHistory || showBeginEndBalanceEnabled
        ? isSummaryLoading
        : isHistoryLoading && !isPageChanged;
    const commonError = summaryError && historyError && 'playtech.mobile.detailedtransactionhistory.error.request';

    const fetchSummary = hooks.useFetchSummary();
    const fetchHistory = hooks.useFetchHistory();
    const {
        filters,
        headerValidate,
        headerInitialValues,
        anotherPeriodFilter,
        onAnotherPeriodApply,
        afterFiltersControls
    } = hooks.useTableHeader();
    const tableSummary = hooks.useSummary();
    const tableData = hooks.useTableData({ isMobileView });
    const loadMore = hooks.useLoadMore();
    const accordionContainerStyle = `${style.accordionContainer} ${isHistoryLoading && !isPageChanged ? style.localLoader : ''}`;

    useEffectOnce(() => {
        dispatch(actions.changePeriod(helpers.getInitialPeriodData()));
    }, [dispatch]);

    useUpdateEffect(() => {
        fetchSummary();
    }, [fetchSummary]);

    useUpdateEffect(() => {
        fetchHistory();
    }, [fetchHistory]);

    return (
        <Table
            filters={filters}
            loadMore={loadMore}
            tableRef={tableRef}
            tableData={tableData}
            isLoading={isLoading}
            tableSummary={tableSummary}
            showTableBody={!historyError}
            headerValidate={headerValidate}
            tableRowView={TABLE_VIEW.ACCORDION}
            emptyTitle={translate('noTransactions')}
            anotherPeriodFilter={anotherPeriodFilter}
            headerInitialValues={headerInitialValues}
            onAnotherPeriodApply={onAnotherPeriodApply}
            afterFiltersControls={afterFiltersControls}
            accordionViewWidthThreshold={MOBILE_VIEW_WIDTH_THRESHOLD}
            error={commonError || summaryError || historyError || exportError}
            classNames={{ ...style, accordionContainer: accordionContainerStyle }}
            summaryRowView={isMobile || isMobileView ? TABLE_VIEW.ACCORDION : TABLE_VIEW.TABLE}
        />
    );
};

export const DetailedTransactionHistory = compose(
    withComponent({ isForLoggedIn: true }),
    withDynamicReducer()
)(DetailedTransactionHistoryBare);

componentService.registerPageComponents({ [DETAILED_TRANSACTION_HISTORY]: DetailedTransactionHistory });
